import { useCallback, useMemo, useReducer, Reducer } from 'react';
import { useMutation } from '@tanstack/react-query';
import { SavedSearchHandler } from './types';
import { SavedSearchDateRangeForm } from '../../search-windows/saved-search';

const RUN_SAVED_SEARCH = 'RUN_SAVED_SEARCH';
const CLOSE_SEARCHES = 'CLOSE_SEARCHES';
const OPEN_SAVED_SEARCH = 'OPEN_SAVED_SEARCH';
const MUTATE_SEARCH = 'MUTATE_SEARCH';
const SET_SEARCH_DATA = 'SET_SEARCH_DATA';
const SET_SAVED_SEARCH_ID = 'SET_SAVED_SEARCH_ID';

interface SearchState<SearchForm> {
    showSavedSearches: boolean;
    uniqueSearch: number;
    savedSearchId?: number;
    searchData?: SearchForm;
    showSearchResults?: boolean;
    dateRangeOverride?: SavedSearchDateRangeForm;
}

type SearchAction<SearchForm> =
    | {
          type: typeof RUN_SAVED_SEARCH;
          payload: {
              search: SearchForm;
              savedSearchId: number;
              dateRangeOverride?: SavedSearchDateRangeForm;
          };
      }
    | { type: typeof CLOSE_SEARCHES }
    | { type: typeof OPEN_SAVED_SEARCH }
    | { type: typeof MUTATE_SEARCH; payload: SearchForm }
    | { type: typeof SET_SEARCH_DATA; payload: SearchForm | undefined }
    | { type: typeof SET_SAVED_SEARCH_ID; payload: number | undefined };

function searchReducer<SearchForm>(
    state: SearchState<SearchForm>,
    action: SearchAction<SearchForm>
): SearchState<SearchForm> {
    switch (action.type) {
        case RUN_SAVED_SEARCH:
            return {
                ...state,
                savedSearchId: action.payload.savedSearchId,
                searchData: action.payload.search,
                dateRangeOverride: action.payload.dateRangeOverride,
                showSearchResults: true,
                showSavedSearches: false,
                uniqueSearch: state.uniqueSearch + 1,
            };
        case CLOSE_SEARCHES:
            return {
                ...state,
                savedSearchId: undefined,
                searchData: undefined,
                showSavedSearches: false,
                showSearchResults: false,
            };

        case OPEN_SAVED_SEARCH:
            return {
                ...state,
                showSavedSearches: true,
                showSearchResults: false,
            };
        case MUTATE_SEARCH:
            return {
                ...state,
                searchData: action.payload,
                showSearchResults: true,
                uniqueSearch: state.uniqueSearch + 1,
            };
        case SET_SEARCH_DATA:
            return {
                ...state,
                searchData: action.payload,
                showSearchResults: true,
            };
        case SET_SAVED_SEARCH_ID:
            return {
                ...state,
                savedSearchId: action.payload,
            };
        default:
            return state;
    }
}

export function useSearchState<FormType, SearchForm>() {
    const [state, dispatch] = useReducer<
        Reducer<SearchState<SearchForm>, SearchAction<SearchForm>>
    >(searchReducer, {
        uniqueSearch: 0,
        savedSearchId: undefined,
        searchData: undefined,
        showSavedSearches: false,
        showSearchResults: false,
    });

    const runSavedSearch: SavedSearchHandler<SearchForm> = useCallback(
        ({ search, savedSearchId, dateRangeOverride }) => {
            dispatch({
                type: RUN_SAVED_SEARCH,
                payload: { search, savedSearchId, dateRangeOverride },
            });
        },
        []
    );

    const closeSearches = useCallback(() => {
        dispatch({ type: CLOSE_SEARCHES });
    }, []);

    const openSavedSearch = useCallback(() => {
        dispatch({ type: OPEN_SAVED_SEARCH });
    }, []);

    const resultsOrSavedShown = useMemo(
        () => !!state.showSavedSearches || !!state.showSearchResults,
        [state.showSavedSearches, state.showSearchResults]
    );

    const mutation = useMutation((searchForm: FormType) => {
        dispatch({ type: MUTATE_SEARCH, payload: (searchForm as unknown) as SearchForm });
        return Promise.resolve();
    });

    const setSearchData = useCallback(({ search }: { search: SearchForm }) => {
        dispatch({ type: SET_SEARCH_DATA, payload: search });
    }, []);

    const setSavedSearchId = useCallback((id: number | undefined) => {
        dispatch({ type: SET_SAVED_SEARCH_ID, payload: id });
    }, []);

    return {
        mutation,
        resultsOrSavedShown,
        runSavedSearch,
        closeSearches,
        openSavedSearch,
        searchData: state.searchData,
        savedSearchId: state.savedSearchId,
        uniqueSearch: state.uniqueSearch,
        showSearchResults: state.showSearchResults,
        showSavedSearches: state.showSavedSearches,
        dateRangeOverride: state.dateRangeOverride,
        setSearchData,
        setSavedSearchId,
    };
}
